import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard, Progress } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { moeda } from '@/comuns/formato';
import { CANAIS_DA_CAMPANHA, PUBLICOS, STATUS_DA_CAMPANHA } from '@/data/marketingDaClinica';
import { dados } from '@/data/porta';
import { PERIODOS_DO_PAINEL } from '@/data/anuncios';
import type { AbaDeMarketing, Campanha, CargaDeMarketing, CargaDoPainelDeAnuncios, LinhaDeOrigem, PublicoDisponivel } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { CampanhaModal } from './marketing/CampanhaModal';
import { DisparoDaCampanha } from './marketing/DisparoDaCampanha';
import { PainelDeAnuncios } from './marketing/PainelDeAnuncios';

const ABAS: AbaDeMarketing[] = ['campanhas', 'ativas', 'publicos', 'criativos', 'anuncios'];

function abaDaRota(pathname: string): AbaDeMarketing {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeMarketing) : 'campanhas';
}

export interface CargaDaTelaDeMarketing extends CargaDeMarketing {
    /** Só na aba Anúncios: a conta da Meta e os números do período (0162). */
    anuncios: CargaDoPainelDeAnuncios | null;
}

export async function carregarMarketing({ request }: LoaderFunctionArgs): Promise<CargaDaTelaDeMarketing> {
    const url = new URL(request.url);
    const aba = abaDaRota(url.pathname);
    const pedido = Number(url.searchParams.get('dias'));
    const dias = (PERIODOS_DO_PAINEL as readonly number[]).includes(pedido) ? pedido : 30;
    const [carga, anuncios] = await Promise.all([
        dados().marketing(aba),
        aba === 'anuncios' ? dados().painelDeAnuncios(dias) : Promise.resolve(null),
    ]);
    return { ...carga, anuncios };
}

export function Marketing() {
    const carga = useLoaderData() as CargaDaTelaDeMarketing;
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const rota = rotaPorId('marketing');
    const [emEdicao, setEmEdicao] = useState<Campanha | 'nova' | null>(null);
    const [disparando, setDisparando] = useState<Campanha | null>(null);

    const colunas: DataTableColumn<Campanha>[] = [
        {
            key: 'nome', header: 'Campanha', sortable: true,
            render: (c) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{c.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {CANAIS_DA_CAMPANHA[c.canal]} · {PUBLICOS[c.publicoTipo].rotulo}
                        {c.publicoParam.origem ? ` (${c.publicoParam.origem})` : ''}
                        {c.publicoParam.dias ? ` (${c.publicoParam.dias} dias)` : ''}
                    </span>
                </span>
            ),
        },
        {
            key: 'publicoTamanho', header: 'Público agora', align: 'right', width: 130,
            render: (c) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{c.publicoTamanho}</span>,
        },
        {
            key: 'leads', header: 'Leads', align: 'right', width: 110,
            /* Sem origem declarada não há atribuição — e um zero aqui diria que
               ninguém chegou, quando o que falta é o vínculo. */
            render: (c) => (c.origemLead
                ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{c.leads} · {c.ganhos} ganhos</span>
                : <span style={{ color: 'var(--text-tertiary)' }}>sem atribuição</span>),
        },
        {
            key: 'investimentoCentavos', header: 'Investido', align: 'right', width: 120,
            render: (c) => (c.investimentoCentavos
                ? moeda(c.investimentoCentavos)
                : <span style={{ color: 'var(--text-tertiary)' }}>não informado</span>),
        },
        {
            key: 'retorno', header: 'Retorno', align: 'right', width: 120,
            render: (c) => (c.retorno === undefined
                ? <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                : <strong style={{ fontWeight: 600 }}>{c.retorno.toLocaleString('pt-BR')}x</strong>),
        },
        {
            key: 'status', header: 'Status', width: 120,
            render: (c) => <Badge tone={STATUS_DA_CAMPANHA[c.status].tom} dot>{STATUS_DA_CAMPANHA[c.status].rotulo}</Badge>,
        },
        {
            key: 'acoes', header: '', width: 190, align: 'right',
            render: (c) => (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                    {/* Só campanha ativa de WhatsApp dispara: é a que tem por onde sair
                        e que alguém já decidiu que está valendo. */}
                    {c.canal === 'whatsapp' && c.status === 'ativa' && (
                        <Button size="sm" onClick={() => setDisparando(c)} iconLeft={<Icon name="send" size={14} />}>Disparar</Button>
                    )}
                    <Button size="sm" variant="secondary" onClick={() => setEmEdicao(c)}>Editar</Button>
                </span>
            ),
        },
    ];

    const colunasDeOrigem: DataTableColumn<LinhaDeOrigem>[] = [
        { key: 'origem', header: 'Origem', render: (o) => <strong style={{ fontWeight: 600 }}>{o.origem}</strong> },
        { key: 'leads', header: 'Leads', align: 'right', width: 90 },
        { key: 'ganhos', header: 'Ganhos', align: 'right', width: 90 },
        { key: 'receitaCentavos', header: 'Receita', align: 'right', width: 130, render: (o) => moeda(o.receitaCentavos) },
        {
            key: 'investimentoCentavos', header: 'Investido', align: 'right', width: 130,
            render: (o) => (o.investimentoCentavos ? moeda(o.investimentoCentavos) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>),
        },
    ];

    const maiorPublico = Math.max(...carga.publicos.map((p: PublicoDisponivel) => p.tamanho), 1);

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Marketing"
                description="Campanhas, públicos e o que cada origem trouxe."
                actions={<Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setEmEdicao('nova')}>Nova campanha</Button>}
            >
                <Tabs
                    ariaLabel="Seções do marketing" activeId={carga.aba}
                    onChange={(id) => navigate(`/app/marketing/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: c.path === 'campanhas' ? carga.totais.campanhas
                            : c.path === 'ativas' ? carga.totais.ativas
                                : c.path === 'publicos' ? carga.totais.publicos : undefined,
                    }))}
                />
            </PageHeader>

            {carga.aba === 'anuncios' && carga.anuncios && (
                <PainelDeAnuncios painel={carga.anuncios} aoMudar={() => { void revalidator.revalidate(); }} />
            )}

            {carga.aba !== 'anuncios' && (
            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>
            )}

            {/* Duas faltas diferentes, ditas separadamente: uma impede enviar, a
                outra impede medir. Juntá-las numa frase só esconderia que a
                atribuição por origem já funciona. */}
            {carga.aba !== 'anuncios' && (
                <Alert tone="info" dismissible={false} title="O que esta tela mede — e o que ela ainda não faz">
                    O resultado destas campanhas é atribuído pela <strong>origem do lead</strong>, que a equipe preenche no CRM, e o
                    investimento é o valor que a clínica informa. Os números da Meta Ads ficam na aba <strong>Anúncios</strong>.
                    {carga.canalConectado
                        ? ' Campanha ativa de WhatsApp é disparada daqui, com confirmação: a mensagem sai pela fila, 40 pessoas a cada 5 minutos.'
                        : ' Disparar uma campanha exige o WhatsApp da clínica conectado.'}
                </Alert>
            )}

            {(carga.aba === 'campanhas' || carga.aba === 'ativas') && (
                <Card padding={0}>
                    <DataTable
                        columns={colunas} rows={carga.campanhas} rowKey="id"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        emptyMessage="Nenhuma campanha nesta aba."
                    />
                </Card>
            )}

            {carga.aba === 'publicos' && (
                <Card title="Regras de público" subtitle="O tamanho é calculado agora, não gravado" padding={18}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {carga.publicos.map((p) => (
                            <div key={p.tipo} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ width: 190, fontSize: 13 }}>
                                    <strong style={{ display: 'block', fontWeight: 600 }}>{p.rotulo}</strong>
                                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{p.descricao}</span>
                                </span>
                                <Progress value={Math.round((p.tamanho / maiorPublico) * 100)} style={{ flex: 1 }} />
                                <span style={{ width: 90, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                                    {p.tamanho} {p.tamanho === 1 ? 'pessoa' : 'pessoas'}
                                </span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {carga.aba === 'criativos' && (
                <Card padding={0}>
                    <EmptyState
                        icon="image"
                        title="Criativos dependem do armazenamento de arquivos"
                        description="Nenhum bucket está ligado a esta clínica. Uma galeria que não guarda arquivo devolveria 'enviado' para uma imagem perdida."
                    />
                </Card>
            )}

            {carga.aba !== 'anuncios' && (
                <Card title="Origem dos leads" subtitle="Leads criados no mês corrente, com o que cada origem trouxe" padding={0}>
                    <DataTable
                        columns={colunasDeOrigem} rows={carga.origens} rowKey="origem"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        emptyMessage="Nenhum lead criado no mês."
                    />
                </Card>
            )}

            <CampanhaModal
                campanha={emEdicao}
                origens={carga.origens.map((o) => o.origem)}
                onClose={() => setEmEdicao(null)}
                onSalvo={() => { setEmEdicao(null); revalidator.revalidate(); }}
            />

            <DisparoDaCampanha
                campanha={disparando}
                canalConectado={carga.canalConectado}
                onClose={() => setDisparando(null)}
                onDisparado={() => { setDisparando(null); revalidator.revalidate(); }}
            />
        </div>
    );
}
